/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

export type ProviderType = 'ollama' | 'openai' | 'azure' | 'gemini' | 'anthropic' | 'compat';
export type ProviderStatus = 'ok' | 'limited' | 'fail' | 'none';

export interface AssistantStatus {
  enabled: boolean;
  /** Whether suggesting a document's metadata is on — never true while `enabled` is false. */
  classify?: boolean;
  /** Whether summarising a document and comparing versions is on — same rule as `classify`. */
  insights?: boolean;
  /** Whether an administrator may have a form drafted from an existing one — same rule as `classify`. */
  drafts?: boolean;
  provider: { name: string; model: string; location: 'local' | 'cloud' } | null;
}

export interface AssistantProvider {
  id?: string;
  type: ProviderType;
  name: string;
  url: string;
  model: string;
  location: 'local' | 'cloud';
  status: ProviderStatus;
  /** What the server shows of a stored key: its last four characters, or empty. */
  keyMask?: string;
  /** Sent only when changed: null keeps the stored key, '' removes it. */
  apiKey?: string | null;
}

export interface AssistantSettings {
  enabled: boolean;
  defaultProviderId: string | null;
  cloudAllowedLevels: string[];
  maxPagesPerQuestion: number;
  historyDays: number;
  autoClassify: boolean;
  docInsights: boolean;
  formDrafts: boolean;
  providers: AssistantProvider[];
}

/** One property of the metadata schema, as handed to the model: what it is called and what it may hold. */
export interface ClassifyField {
  id: string;
  label: string;
  dataType: string;
  options?: { value: string; label: string }[];
}

/** Why a document got no suggestions, when the service says. */
export type ClassifyReason = 'no-text' | 'confidentiality' | 'nothing-found';

export interface ClassifyResult {
  /** Property id → the value to offer. Only fields that fit the schema are here. */
  fields: Record<string, string>;
  reason?: ClassifyReason | null;
  provider?: string;
  model?: string;
}

/** Why an insight call answered with nothing: nothing readable, or the level may not leave the building. */
export type InsightReason = 'no-text' | 'confidentiality';

export interface InsightResult {
  summary?: string | null;
  changes?: string | null;
  reason?: InsightReason | null;
  provider?: string;
  model?: string;
  pages?: number;
  ms?: number;
}

/** A form as the model read it, already cut down by the service to what the builder can hold. */
export interface FormDraftField {
  label: string;
  type: string;
  required: boolean;
  help?: string | null;
  options?: string[] | null;
  columns?: { label: string; type: string }[] | null;
  text?: string | null;
}

export interface FormDraft {
  title: string;
  description?: string | null;
  organisation?: string | null;
  sections: { title?: string | null; columns: number; fields: FormDraftField[] }[];
  signatures: string[];
}

export type FormDraftReason = 'no-text' | 'confidentiality' | 'nothing-found';

export interface FormDraftResult {
  draft: FormDraft | null;
  reason?: FormDraftReason | null;
  provider?: string;
  model?: string;
  ms?: number;
}

export interface TestStep {
  title: string;
  ok: boolean;
  warn: boolean;
  detail: string;
  ms: number;
}

export interface TestResult {
  status: ProviderStatus;
  steps: TestStep[];
}

export interface ConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  turnCount: number;
}

export interface Citation {
  n: number;
  marker: string;
  ref: string;
  nodeId: string;
  name: string;
  path: string;
  page: number | null;
  /** True when the answer cited this page itself; false when it is only a page that was read. */
  inline?: boolean;
}

/** What the chat stream says, one event at a time. */
export type AssistantEvent =
  | { type: 'step.start'; id: string; kind: string; title: string; sig?: string | null }
  | { type: 'step.end'; id: string; summary: string; lines?: string[] | null; ms: number; ok: boolean }
  | { type: 'permission'; allowed?: number; withheld?: number }
  | { type: 'answer.delta'; text: string }
  | { type: 'answer.reset' }
  | ({ type: 'citation' } & Citation)
  | { type: 'done'; ms: number; pagesRead: number; documents: number; provider: string; model: string }
  | { type: 'saved'; conversationId: string }
  | { type: 'error'; message: string };

/**
 * Talks to the assistant on the OCR sidecar (`services/aca-ocr-api`).
 *
 * Every call carries the reader's own ticket. The sidecar asks Alfresco who
 * that is before it does anything, so an administrator's screen and a reader's
 * chat are told apart there, not here — and the model only ever sees documents
 * this person could open themselves.
 */
@Injectable({ providedIn: 'root' })
export class AssistantService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);
  private readonly config = inject(AppConfigService);

  /** Whether the corner button shows, and which model will answer. */
  readonly status = signal<AssistantStatus | null>(null);

  private get base(): string {
    return this.config.get<string>('ocr.baseUrl', '/ocr-api') + '/api/assistant';
  }

  private authorization(): string | null {
    const ticket = this.auth.getToken();
    return ticket ? 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) : null;
  }

  private headers(): HttpHeaders {
    const value = this.authorization();
    return value ? new HttpHeaders({ Authorization: value }) : new HttpHeaders();
  }

  refreshStatus(): void {
    this.http.get<AssistantStatus>(`${this.base}/status`).subscribe({
      next: (status) => this.status.set(status),
      error: () => this.status.set({ enabled: false, provider: null })
    });
  }

  settings(): Observable<AssistantSettings> {
    return this.http.get<AssistantSettings>(`${this.base}/settings`, { headers: this.headers() });
  }

  saveSettings(settings: AssistantSettings): Observable<AssistantSettings> {
    return this.http.put<AssistantSettings>(`${this.base}/settings`, settings, { headers: this.headers() }).pipe(tap(() => this.refreshStatus()));
  }

  models(provider: AssistantProvider): Observable<{ models: string[] }> {
    return this.http.post<{ models: string[] }>(`${this.base}/models`, provider, { headers: this.headers() });
  }

  test(provider: AssistantProvider): Observable<TestResult> {
    return this.http.post<TestResult>(`${this.base}/test`, provider, { headers: this.headers() });
  }

  /**
   * Asks for suggested metadata for one document. Nothing is saved: the values
   * come back for a person to review, and they stay in the form until saved.
   */
  classify(nodeId: string, fields: ClassifyField[]): Observable<ClassifyResult> {
    return this.http.post<ClassifyResult>(`${this.base}/classify`, { nodeId, fields }, { headers: this.headers() });
  }

  /**
   * The same suggestion for a file not uploaded yet: its text, read in this
   * browser, and the confidentiality chosen for it in the form, which decides
   * whether a cloud model may see it.
   */
  classifyText(name: string, text: string, confidentiality: string, fields: ClassifyField[]): Observable<ClassifyResult> {
    return this.http.post<ClassifyResult>(`${this.base}/classify-text`, { name, text, confidentiality, fields }, { headers: this.headers() });
  }

  /** The document's indexed text read down to a few lines. Nothing is written. */
  summarize(nodeId: string): Observable<InsightResult> {
    return this.http.post<InsightResult>(`${this.base}/summarize`, { nodeId }, { headers: this.headers() });
  }

  /**
   * What differs between two texts of one document — normally two of its
   * versions. The caller supplies both texts: the index only holds the current
   * version, and an older version's bytes are read in the browser.
   */
  compareVersions(nodeId: string, a: { label: string; text: string }, b: { label: string; text: string }): Observable<InsightResult> {
    return this.http.post<InsightResult>(
      `${this.base}/compare`,
      { nodeId, labelA: a.label, textA: a.text, labelB: b.label, textB: b.text },
      { headers: this.headers() }
    );
  }

  /**
   * A draft form read from an existing one: the text of a paper form, a Word
   * file or a PDF, read in this browser. Nothing is saved — the draft opens in
   * the builder for an administrator to check.
   */
  draftForm(name: string, text: string, confidentiality: string): Observable<FormDraftResult> {
    return this.http.post<FormDraftResult>(`${this.base}/draft-form`, { name, text, confidentiality }, { headers: this.headers() });
  }

  history(): Observable<{ conversations: ConversationSummary[] }> {
    return this.http.get<{ conversations: ConversationSummary[] }>(`${this.base}/history`, { headers: this.headers() });
  }

  conversation(id: string): Observable<any> {
    return this.http.get<any>(`${this.base}/history/${encodeURIComponent(id)}`, { headers: this.headers() });
  }

  deleteConversation(id: string): Observable<unknown> {
    return this.http.delete(`${this.base}/history/${encodeURIComponent(id)}`, { headers: this.headers() });
  }

  /**
   * Asks one question. The answer is a server-sent event stream; each event is
   * handed over as it arrives, so the window can draw the steps while the model
   * is still working. `fetch` rather than `EventSource`, because the question is
   * POSTed and carries an Authorization header, neither of which EventSource can.
   */
  async chat(
    body: { question: string; conversationId?: string | null; documentId?: string | null },
    onEvent: (event: AssistantEvent) => void,
    abortSignal: AbortSignal
  ): Promise<void> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const authorization = this.authorization();
    if (authorization) {
      headers['Authorization'] = authorization;
    }

    const response = await fetch(`${this.base}/chat`, { method: 'POST', headers, body: JSON.stringify(body), signal: abortSignal });
    if (!response.ok || !response.body) {
      onEvent({ type: 'error', message: `HTTP ${response.status}` });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

      let cut = buffer.indexOf('\n\n');
      while (cut >= 0) {
        const event = AssistantService.parse(buffer.slice(0, cut));
        buffer = buffer.slice(cut + 2);
        if (event) {
          onEvent(event);
        }
        cut = buffer.indexOf('\n\n');
      }
    }
  }

  private static parse(block: string): AssistantEvent | null {
    let name = '';
    let data = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) {
        name = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        data += line.slice(5).trim();
      }
    }
    if (!name) {
      return null;
    }
    try {
      return { type: name, ...(data ? JSON.parse(data) : {}) } as AssistantEvent;
    } catch {
      return null;
    }
  }
}
